import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BonusCard } from './components/BonusCard';
import { Footer } from './components/Footer';
import { CheckIcon, Heart, Star, ShieldCheck, Sparkles, BookOpen } from 'lucide-react';
// Simplified animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function App() {
  return <div className="min-h-screen bg-[#FAFAF9] text-stone-800 font-sans selection:bg-amber-100 selection:text-amber-900" data-id="element-0">
      {/* HERO SECTION */}
      <header className="pt-20 pb-16 px-4 md:pt-32 md:pb-24 bg-white border-b border-stone-100" data-id="element-1">
        <div className="max-w-5xl mx-auto text-center" data-id="element-2">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-3">
            <motion.div variants={fadeInUp} className="flex justify-center" data-id="element-4">
              <span className="inline-flex items-center gap-2 py-1 px-4 rounded-full bg-amber-50 text-amber-800 text-sm font-medium border border-amber-100" data-id="element-5">
                <Sparkles className="w-4 h-4" data-id="element-6" />
                Crianza Respetuosa y Consciente
              </span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl font-bold text-stone-900 leading-[1.1] tracking-tight max-w-4xl mx-auto" data-id="element-7">
              Cría hijos emocionalmente fuertes,{' '}
              <span className="text-amber-600 relative inline-block" data-id="element-8">
                sin gritos ni culpa
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-amber-200 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-9">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" data-id="element-10" />
                </svg>
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed" data-id="element-11">
              La guía práctica para mamás que quieren educar desde el amor, la
              calma y la conexión. Aprende a gestionar berrinches y emociones
              difíciles sin perder la paciencia.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4" data-id="element-12">
              <a href="https://crianza.amordemama.com" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg py-4 px-8 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 text-center" data-id="element-13">
                QUIERO LA GUÍA AHORA
              </a>
              <p className="text-sm text-stone-500 font-medium" data-id="element-14">
                ⚡️ Descarga inmediata + 3 bonos gratis
              </p>
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* SOCIAL PROOF / PROMISE */}
      <section className="py-12 px-4 bg-stone-50 border-b border-stone-100" data-id="element-15">
        <div className="max-w-4xl mx-auto text-center" data-id="element-16">
          <p className="text-stone-600 font-medium mb-8" data-id="element-17">
            Lo que lograrás con este método:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-18">
            {[{
            icon: '😌',
            text: 'Más paciencia'
          }, {
            icon: '🧠',
            text: 'Inteligencia emocional'
          }, {
            icon: '🤝',
            text: 'Conexión profunda'
          }, {
            icon: '🏠',
            text: 'Hogar en calma'
          }].map((item, i) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-19">
                <span className="text-2xl" data-id="element-20">{item.icon}</span>
                <span className="text-stone-800 font-semibold" data-id="element-21">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* PROBLEM AWARENESS */}
      <section className="py-20 px-4 bg-white" data-id="element-22">
        <div className="max-w-5xl mx-auto" data-id="element-23">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-24">
            <div className="order-2 md:order-1" data-id="element-25">
              <h2 className="text-3xl font-bold text-stone-900 mb-6" data-id="element-26">
                ¿Te sientes identificada?
              </h2>
              <div className="space-y-4" data-id="element-27">
                {['Terminas el día agotada y con culpa por haber gritado.', 'Tus hijos tienen berrinches y no sabes cómo calmarlos.', 'Quieres educar diferente a tus padres pero te faltan herramientas.', 'Sientes que pierdes el control cuando tus hijos no obedecen.'].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-stone-50 border border-stone-100" data-id="element-28">
                    <div className="bg-red-100 text-red-500 p-1 rounded-full mt-0.5" data-id="element-29">
                      <CheckIcon className="w-4 h-4" data-id="element-30" />
                    </div>
                    <p className="text-stone-700" data-id="element-31">{item}</p>
                  </div>)}
              </div>
              <div className="mt-8 p-6 bg-amber-50 rounded-xl border border-amber-100" data-id="element-32">
                <p className="text-amber-900 font-medium" data-id="element-33">
                  💛 No es tu culpa. Nadie nos enseñó a gestionar las emociones
                  de nuestros hijos (ni las nuestras). Pero hoy puedes cambiar
                  eso.
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2 flex justify-center" data-id="element-34">
              <div className="relative w-full max-w-md aspect-[3/4] bg-stone-100 rounded-2xl overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-35">
                <img src="https://cdn.magicpatterns.com/uploads/sKBV5ZErtpNxvyPHktR17k/IMG_0179.jpg" alt="Madre e hijo leyendo juntos" className="w-full h-full object-cover" data-id="element-36" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN PRODUCT SHOWCASE */}
      <section className="py-20 px-4 bg-[#FFF9E6]" data-id="element-37">
        <div className="max-w-6xl mx-auto" data-id="element-38">
          <div className="text-center mb-16" data-id="element-39">
            <span className="text-amber-600 font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-40">
              La Solución Completa
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-41">
              Guía de Crianza con Inteligencia Emocional
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto text-lg" data-id="element-42">
              Un manual práctico, directo y lleno de ejemplos reales para
              transformar la relación con tus hijos.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-stone-100 flex flex-col md:flex-row gap-12 items-center" data-id="element-43">
            <div className="w-full md:w-1/2" data-id="element-44">
              <img src="https://cdn.magicpatterns.com/uploads/sKBV5ZErtpNxvyPHktR17k/IMG_0179.jpg" alt="Portada de la Guía de Crianza" className="w-full rounded-xl shadow-lg transform md:-rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-45" />
            </div>
            <div className="w-full md:w-1/2 space-y-6" data-id="element-46">
              <h3 className="text-2xl font-bold text-stone-800" data-id="element-47">
                ¿Qué aprenderás?
              </h3>
              <ul className="space-y-4" data-id="element-48">
                {['Cómo validar emociones sin ser permisiva.', 'Herramientas para gestionar berrinches en público y en casa.', 'Guiones exactos de qué decir en momentos de crisis.', 'Técnicas para mantener tu propia calma cuando todo explota.', 'Cómo establecer límites firmes pero amorosos.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-49">
                    <div className="bg-green-100 text-green-600 p-1 rounded-full mt-1" data-id="element-50">
                      <CheckIcon className="w-3 h-3" data-id="element-51" />
                    </div>
                    <span className="text-stone-700 text-lg" data-id="element-52">{item}</span>
                  </li>)}
              </ul>
              <div className="pt-6" data-id="element-53">
                <a href="https://crianza.amordemama.com" target="_blank" rel="noopener noreferrer" className="block w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all hover:scale-105 text-center" data-id="element-54">
                  QUIERO EMPEZAR A LEER
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-20 px-4 bg-stone-50 border-y border-stone-100" data-id="element-55">
        <div className="max-w-6xl mx-auto" data-id="element-56">
          <div className="text-center mb-16" data-id="element-57">
            <span className="text-amber-600 font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-58">
              Historias Reales
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-59">
              Mamás que ya transformaron su crianza
            </h2>
            <div className="flex justify-center gap-1 text-amber-400 mb-2" data-id="element-60">
              <Star className="w-6 h-6 fill-current" data-id="element-61" />
              <Star className="w-6 h-6 fill-current" data-id="element-62" />
              <Star className="w-6 h-6 fill-current" data-id="element-63" />
              <Star className="w-6 h-6 fill-current" data-id="element-64" />
              <Star className="w-6 h-6 fill-current" data-id="element-65" />
            </div>
            <p className="text-stone-600" data-id="element-66">Más de 500 mamás felices</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-67">
            {/* Testimonio 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 relative" data-id="element-68">
              <div className="absolute -top-4 left-8 bg-amber-100 text-amber-600 p-2 rounded-lg" data-id="element-69">
                <span className="text-2xl" data-id="element-70">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-71">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-72">
                  <Star className="w-4 h-4 fill-current" data-id="element-73" />
                  <Star className="w-4 h-4 fill-current" data-id="element-74" />
                  <Star className="w-4 h-4 fill-current" data-id="element-75" />
                  <Star className="w-4 h-4 fill-current" data-id="element-76" />
                  <Star className="w-4 h-4 fill-current" data-id="element-77" />
                </div>
                <h3 className="font-bold text-stone-800 text-lg" data-id="element-78">
                  Transformación emocional
                </h3>
              </div>
              <p className="text-stone-600 mb-6 italic leading-relaxed" data-id="element-79">
                "Esta guía llegó justo cuando sentía que estaba fallando como
                mamá. Aprendí a escuchar a mis hijos sin gritar y a manejar mis
                propias emociones. Hoy mi casa se siente más tranquila y
                conectada. No es solo una guía, es un abrazo para el alma."
              </p>
              <div className="flex items-center gap-3 border-t border-stone-50 pt-4" data-id="element-80">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold" data-id="element-81">
                  MG
                </div>
                <div data-id="element-82">
                  <p className="font-bold text-stone-800 text-sm" data-id="element-83">Mariana G.</p>
                  <p className="text-stone-500 text-xs" data-id="element-84">Mamá de 2 niños</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 relative" data-id="element-85">
              <div className="absolute -top-4 left-8 bg-amber-100 text-amber-600 p-2 rounded-lg" data-id="element-86">
                <span className="text-2xl" data-id="element-87">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-88">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-89">
                  <Star className="w-4 h-4 fill-current" data-id="element-90" />
                  <Star className="w-4 h-4 fill-current" data-id="element-91" />
                  <Star className="w-4 h-4 fill-current" data-id="element-92" />
                  <Star className="w-4 h-4 fill-current" data-id="element-93" />
                  <Star className="w-4 h-4 fill-current" data-id="element-94" />
                </div>
                <h3 className="font-bold text-stone-800 text-lg" data-id="element-95">
                  Resultados prácticos
                </h3>
              </div>
              <p className="text-stone-600 mb-6 italic leading-relaxed" data-id="element-96">
                "Lo que más me gustó es que no es teoría complicada. Son
                palabras exactas para usar en momentos difíciles. Las tarjetas
                emocionales cambiaron por completo la forma en que mis hijos
                expresan lo que sienten."
              </p>
              <div className="flex items-center gap-3 border-t border-stone-50 pt-4" data-id="element-97">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold" data-id="element-98">
                  LP
                </div>
                <div data-id="element-99">
                  <p className="font-bold text-stone-800 text-sm" data-id="element-100">Laura P.</p>
                  <p className="text-stone-500 text-xs" data-id="element-101">Mamá y educadora</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 relative" data-id="element-102">
              <div className="absolute -top-4 left-8 bg-amber-100 text-amber-600 p-2 rounded-lg" data-id="element-103">
                <span className="text-2xl" data-id="element-104">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-105">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-106">
                  <Star className="w-4 h-4 fill-current" data-id="element-107" />
                  <Star className="w-4 h-4 fill-current" data-id="element-108" />
                  <Star className="w-4 h-4 fill-current" data-id="element-109" />
                  <Star className="w-4 h-4 fill-current" data-id="element-110" />
                  <Star className="w-4 h-4 fill-current" data-id="element-111" />
                </div>
                <h3 className="font-bold text-stone-800 text-lg" data-id="element-112">
                  Alivio para la mamá
                </h3>
              </div>
              <p className="text-stone-600 mb-6 italic leading-relaxed" data-id="element-113">
                "Por primera vez sentí que alguien entendía lo agotador que es
                criar con amor. La guía de relajación para mamás fue mi
                salvación. Ahora reacciono desde la calma y no desde el
                cansancio."
              </p>
              <div className="flex items-center gap-3 border-t border-stone-50 pt-4" data-id="element-114">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold" data-id="element-115">
                  AR
                </div>
                <div data-id="element-116">
                  <p className="font-bold text-stone-800 text-sm" data-id="element-117">Andrea R.</p>
                  <p className="text-stone-500 text-xs" data-id="element-118">Mamá primeriza</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 relative" data-id="element-119">
              <div className="absolute -top-4 left-8 bg-amber-100 text-amber-600 p-2 rounded-lg" data-id="element-120">
                <span className="text-2xl" data-id="element-121">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-122">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-123">
                  <Star className="w-4 h-4 fill-current" data-id="element-124" />
                  <Star className="w-4 h-4 fill-current" data-id="element-125" />
                  <Star className="w-4 h-4 fill-current" data-id="element-126" />
                  <Star className="w-4 h-4 fill-current" data-id="element-127" />
                  <Star className="w-4 h-4 fill-current" data-id="element-128" />
                </div>
                <h3 className="font-bold text-stone-800 text-lg" data-id="element-129">
                  Confianza y conexión
                </h3>
              </div>
              <p className="text-stone-600 mb-6 italic leading-relaxed" data-id="element-130">
                "Después de aplicar esta guía, mi hijo empezó a hablar de sus
                emociones sin miedo. Yo también aprendí a perdonarme y a confiar
                en mi proceso como mamá. La recomiendo a todas."
              </p>
              <div className="flex items-center gap-3 border-t border-stone-50 pt-4" data-id="element-131">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold" data-id="element-132">
                  CM
                </div>
                <div data-id="element-133">
                  <p className="font-bold text-stone-800 text-sm" data-id="element-134">
                    Carolina M.
                  </p>
                  <p className="text-stone-500 text-xs" data-id="element-135">
                    Mamá de un niño sensible
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-20 px-4 bg-white" data-id="element-136">
        <div className="max-w-6xl mx-auto" data-id="element-137">
          <div className="text-center mb-12" data-id="element-138">
            <span className="bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-bold mb-4 inline-block" data-id="element-139">
              🎁 REGALOS EXCLUSIVOS
            </span>
            <h2 className="text-3xl font-bold text-stone-900" data-id="element-140">
              Lévate estos 3 bonos GRATIS hoy
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-141">
            <BonusCard title="Tarjetas Emocionales Ilustradas" value="$35" icon="🎨" description="💡 Perfectas para el hogar, rutinas y momentos difíciles." imageUrl="https://cdn.magicpatterns.com/uploads/urr9uCPGXFn9ek73skaMbG/IMG_0180.jpg" benefits={['Identificar lo que sienten', 'Expresar emociones sin berrinches', 'Desarrollar inteligencia emocional desde pequeños']} delay={0.1} data-id="element-142" />
            <BonusCard title="Cómo Crear Tus Propias Tarjetas Emocionales en Casa" value="$47" icon="✂️" description="✨ Una habilidad que podrás usar para siempre." imageUrl="https://cdn.magicpatterns.com/uploads/tDs3nGtTJ1vuHJUsdaKjBf/IMG_0181.jpg" benefits={['Diseñar tarjetas personalizadas según la edad de tus hijos', 'Adaptarlas a situaciones reales del día a día', 'Convertirlas en una herramienta de conexión familiar']} delay={0.2} data-id="element-143" />
            <BonusCard title="Relajación Emocional para Mamás" value="$55" icon="🧘‍♀️" description="🌿 Diseñado para mamás reales, con poco tiempo." imageUrl="https://cdn.magicpatterns.com/uploads/kkUVLS6SFZZtWuAbAjNMKE/IMG_0182.jpg" benefits={['Ejercicios de descarga emocional', 'Técnicas de calma después de días difíciles', 'Prácticas cortas para recuperar energía mental']} delay={0.3} data-id="element-144" />
          </div>
        </div>
      </section>

      {/* PRICING / CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 text-stone-800 relative overflow-hidden" data-id="element-145">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#f59e0b_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-146"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-147">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-stone-900" data-id="element-148">
            Transforma tu maternidad hoy mismo
          </h2>
          <p className="text-stone-700 text-xl mb-12 max-w-2xl mx-auto" data-id="element-149">
            Deja atrás la culpa y los gritos. Empieza a construir la relación
            que siempre soñaste tener con tus hijos.
          </p>

          <div className="bg-white text-stone-900 rounded-3xl p-8 md:p-12 max-w-lg mx-auto shadow-2xl border border-amber-100" data-id="element-150">
            <div className="flex justify-center items-baseline gap-2 mb-2" data-id="element-151">
              <span className="text-5xl font-bold text-amber-600" data-id="element-152">$12.75</span>
              <span className="text-stone-400 line-through text-xl" data-id="element-153">$137</span>
            </div>
            <p className="text-stone-500 mb-8 font-medium" data-id="element-154">
              Oferta por tiempo limitado
            </p>

            <ul className="text-left space-y-3 mb-8 max-w-xs mx-auto" data-id="element-155">
              <li className="flex items-center gap-3" data-id="element-156">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-157" />
                <span data-id="element-158">Guía Digital Completa (PDF)</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-159">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-160" />
                <span data-id="element-161">Bono 1-Tarjetas Emocionales Ilustradas</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-162">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-163" />
                <span data-id="element-164">
                  Bono 2-Cómo Crear Tus Propias Tarjetas Emocionales en Casa
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-165">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-166" />
                <span data-id="element-167">Bono 3-Relajación Emocional para Mamás</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-168">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-169" />
                <span data-id="element-170">Acceso de por vida</span>
              </li>
            </ul>

            <a href="https://crianza.amordemama.com" target="_blank" rel="noopener noreferrer" className="block w-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl py-4 px-8 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-171">
              DESCARGAR TODO AHORA
            </a>
            <p className="mt-4 text-xs text-stone-400" data-id="element-172">
              🔒 Pago 100% seguro • Acceso de por vida
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-173" />
    </div>;
}